/**
 * LegalPage — renders a markdown legal/policy document as a full page.
 *
 * Sources are the hand-authored .md files at `frontend/src/legal/*.md`.
 * Vite's `?raw` suffix imports them as plain strings at build time, so the
 * markdown is bundled alongside the rest of the app (no runtime fetch, no CORS
 * surprises on custom domains, and no divergence between environments).
 *
 * Routes are mounted from `router/index.tsx` as `/legal/:slug`.
 */
import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { PageMeta } from '../components/PageMeta';
import { LegalShell, LegalContainer, LegalContent, LegalTocHint } from './LegalPage.styled';

// Bundled markdown — see JSDoc at top of file for the ?raw rationale.
import termsMd from '../legal/vellum_terms_of_service.md?raw';
import privacyMd from '../legal/vellum_privacy_policy.md?raw';
import communityMd from '../legal/vellum_community_guidelines.md?raw';
import csamMd from '../legal/vellum_csam_policy.md?raw';
import cookiesMd from '../legal/vellum_cookie_policy.md?raw';
import dmcaMd from '../legal/vellum_dmca_policy.md?raw';
import refundsMd from '../legal/vellum_refund_policy.md?raw';

export type LegalSlug =
  | 'terms'
  | 'privacy'
  | 'community-guidelines'
  | 'csam-policy'
  | 'cookies'
  | 'dmca'
  | 'refunds';

// Slug → i18n key under `legal.docs.*`. Hyphenated slugs map to camelCase
// i18n keys so the key path is well-formed without needing quoted segments.
const LEGAL_I18N_KEYS: Record<LegalSlug, string> = {
  terms: 'terms',
  privacy: 'privacy',
  'community-guidelines': 'communityGuidelines',
  'csam-policy': 'csamPolicy',
  cookies: 'cookies',
  dmca: 'dmca',
  refunds: 'refunds',
};

const LEGAL_CONTENT: Record<LegalSlug, string> = {
  terms: termsMd,
  privacy: privacyMd,
  'community-guidelines': communityMd,
  'csam-policy': csamMd,
  cookies: cookiesMd,
  dmca: dmcaMd,
  refunds: refundsMd,
};

function isLegalSlug(value: string | undefined): value is LegalSlug {
  return (
    value === 'terms' ||
    value === 'privacy' ||
    value === 'community-guidelines' ||
    value === 'csam-policy' ||
    value === 'cookies' ||
    value === 'dmca' ||
    value === 'refunds'
  );
}

export default function LegalPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  if (!isLegalSlug(slug)) {
    // Unknown legal slug → redirect to terms rather than a 404, so stale
    // external links (e.g. /legal/tos) still land somewhere useful.
    return <Navigate to="/legal/terms" replace />;
  }

  const doc = LEGAL_CONTENT[slug];
  const i18nKey = LEGAL_I18N_KEYS[slug];
  const title = t(`legal.docs.${i18nKey}.title`);
  const description = t(`legal.docs.${i18nKey}.description`);

  return (
    <>
      <PageMeta title={title} description={description} />
      <LegalShell>
        <LegalContainer>
          <LegalTocHint>
            {t('legal.tocHint', {
              defaultValue: 'Use ⌘/Ctrl+F to search this page.',
            })}
          </LegalTocHint>
          <LegalContent>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc}</ReactMarkdown>
          </LegalContent>
        </LegalContainer>
      </LegalShell>
    </>
  );
}
